import {
  Post,
  PostStatus,
  PostSource,
  PostPriority,
  PostManagementLevel,
  DEFAULT_POST_STATUSES,
  ONPE_POST_STATUSES,
  ConflictStatus,
} from './post';
import { Form, FormStatusScheme } from '../form/form';
import { User } from '@eview/core/models/user';
import * as moment from 'moment';
import { MapContext } from '../../models/map';
import { Tenant } from '@eview/core/models/tenant';

const NormalizeStatusScheme = (
  scheme?: string | FormStatusScheme | null
): FormStatusScheme =>
  scheme === FormStatusScheme.OnpeMace || scheme === 'onpe_mace'
    ? FormStatusScheme.OnpeMace
    : FormStatusScheme.Default;

const GetStatusSchemeFromForm = (
  form?: Form | null
): FormStatusScheme => NormalizeStatusScheme(form?.status_scheme);

const GetStatusesForScheme = (
  scheme?: string | FormStatusScheme | null
): PostStatus[] => {
  const normalized = NormalizeStatusScheme(scheme);
  return normalized === FormStatusScheme.OnpeMace
    ? [...ONPE_POST_STATUSES]
    : [...DEFAULT_POST_STATUSES];
};

/** When no form is selected: union of both schemes for filters/legend. */
const GetAllWorkflowStatuses = (): PostStatus[] => [
  ...DEFAULT_POST_STATUSES,
  ...ONPE_POST_STATUSES
];

const GetStatusLabel = (status: PostStatus | string) => {
  switch (status) {
    case PostStatus.Verified:
      return 'POST_STATUS_VERIFIED';
    case PostStatus.Unverified:
      return 'POST_STATUS_UNVERIFIED';
    case PostStatus.Responded:
      return 'POST_STATUS_RESPONDED';
    case PostStatus.Evaluated:
      return 'POST_STATUS_EVALUATED';
    case PostStatus.Pending:
      return 'POST_STATUS_PENDING';
    case PostStatus.InProgressVerification:
      return 'POST_STATUS_VERIFICATION_IN_PROGRESS';
    case PostStatus.Recibido:
      return 'POST_STATUS_RECIBIDO';
    case PostStatus.Monitoreado:
      return 'POST_STATUS_MONITOREADO';
    case PostStatus.Analizado:
      return 'POST_STATUS_ANALIZADO';
    case PostStatus.Derivado:
      return 'POST_STATUS_DERIVADO';
    case PostStatus.Cerrado:
      return 'POST_STATUS_CERRADO';
    default:
      return 'POST_STATUS_ALL';
  }
};

/** Map publish toggle is available at any workflow status. */
const CanPublishOnMap = (_status?: PostStatus | string): boolean => true;

const GetConflictStatusLabel = (status: ConflictStatus | string) => {
  switch (status) {
    case ConflictStatus.Emergente:
      return 'CONFLICT_STATUS_EMERGENTE';
    case ConflictStatus.Activo:
      return 'CONFLICT_STATUS_ACTIVO';
    case ConflictStatus.Escalado:
      return 'CONFLICT_STATUS_ESCALADO';
    case ConflictStatus.EnNegociacion:
      return 'CONFLICT_STATUS_EN_NEGOCIACION';
    case ConflictStatus.Resuelto:
      return 'CONFLICT_STATUS_RESUELTO';
    case ConflictStatus.Reactivado:
      return 'CONFLICT_STATUS_REACTIVADO';
    default:
      return 'CONFLICT_STATUS_UNKNOWN';
  }
};

const IsOnpeStatus = (status: PostStatus | string): boolean =>
  (ONPE_POST_STATUSES as string[]).includes(status as string);

/** Empty select / placeholder sentinels — not real user text. */
const isPlaceholderFieldValue = (value: unknown): boolean =>
  value == null ||
  value === '' ||
  value === -1 ||
  value === '-1';

const HasDisplayablePostContent = (content: string | null | undefined): boolean => {
  const text = (content ?? '').trim();
  return text.length > 0 && !isPlaceholderFieldValue(text);
};

/** Mapea criticidad legacy del backend al esquema actual. */
const NormalizePostPriority = (
  priority?: string | PostPriority | null
): PostPriority => {
  switch (priority) {
    case PostPriority.Standard:
      return PostPriority.Low;
    case PostPriority.Urgent:
      return PostPriority.Critical;
    case PostPriority.Low:
    case PostPriority.Medium:
    case PostPriority.High:
    case PostPriority.Critical:
      return priority;
    default:
      return PostPriority.Low;
  }
};

const GetPostPriorityLebel = (priority: PostPriority) => {
  switch (NormalizePostPriority(priority)) {
    case PostPriority.Critical:
      return 'PRIORITY_CRITICAL';
    case PostPriority.High:
      return 'PRIORITY_HIGH';
    case PostPriority.Medium:
      return 'PRIORITY_MEDIUM';
    case PostPriority.Low:
    default:
      return 'PRIORITY_LOW';
  }
};

/** CSS class suffix aligned with list/card priority colors (low/medium/high/critical). */
const GetPriorityColorClass = (
  priority?: string | PostPriority | null
): string => {
  switch (NormalizePostPriority(priority)) {
    case PostPriority.Critical:
      return 'priority-critical';
    case PostPriority.High:
      return 'priority-high';
    case PostPriority.Medium:
      return 'priority-medium';
    case PostPriority.Low:
    default:
      return 'priority-low';
  }
};

const GetSourceLabel = (source: PostSource) => {
  switch (source) {
    case PostSource.SMS:
      return 'POST_SOURCE_SMS';
    case PostSource.Twitter:
      return 'POST_SOURCE_TWITTER';
    case PostSource.Email:
      return 'POST_SOURCE_EMAIL';
    default:
    case PostSource.Web:
      return 'POST_SOURCE_WEB';
  }
};

const FormatStatus = (post: Post): string => GetStatusLabel(post.status);
const FormatSource = (post: Post): string => GetSourceLabel(post.source);

const FormatPriority = (post: Post): string =>
  GetPostPriorityLebel(NormalizePostPriority(post.priority));

const FormatDate = (post: Post, date: keyof Post, format: string) => {
  return moment.utc(post[date]).local().format(format);
}

const FormatAuditValue = (post: any, postDate: any, format: string) => {
  const value = JSON.parse(post);
  if (value && value['created'] !== null) {
    return moment.unix(value['created']).format(format);
  } else {
    const dateObj = (postDate === null) ? new Date() : new Date(postDate);
    return dateObj.getDate() + '/' + (dateObj.getMonth() + 1) + '/' + dateObj.getFullYear();
  }
}

const ParseMgmtLevels = (
  actual: MapContext,
  tenant: Tenant
): { mgmt_lev_1: string; mgmt_lev_2: string; mgmt_lev_3:string; UBI_INEI?: string; UBI_ONPE?: string;} => {
  if (!actual)
    return {
      mgmt_lev_1: '',
      mgmt_lev_2: '',
      mgmt_lev_3: ''
    };
  const properties = actual.properties || {};
  
  if (properties.NOMPRO || properties.NOMDIS || properties.NOMDEP) {
    return {
      mgmt_lev_1: properties.NOMPRO || '',
      mgmt_lev_2: properties.NOMDIS || '',
      mgmt_lev_3: properties.NOMDEP || '',
      UBI_INEI: properties.UBI_INEI || '',
      UBI_ONPE: properties.UBI_ONPE || ''
    };
  }
  
  if (properties.mgmt_lev_1 || properties.mgmt_lev_2 || properties.mgmt_lev_3) {
    return {
      mgmt_lev_1: properties.mgmt_lev_1 || '',
      mgmt_lev_2: properties.mgmt_lev_2 || '',
      mgmt_lev_3: properties.mgmt_lev_3 || '',
      UBI_INEI: properties.UBI_INEI || '',
      UBI_ONPE: properties.UBI_ONPE || ''
    };
  }
  
  return {
    mgmt_lev_1: properties[tenant.map_shapefiles.map.mapLevelMap.mgmt_lev_1.key] || '',
    mgmt_lev_2: properties[tenant.map_shapefiles.map.mapLevelMap.mgmt_lev_2.key] || '',
    mgmt_lev_3: properties[tenant.map_shapefiles.map.mapLevelMap.mgmt_lev_3.key] || ''
  };
};

const FormatMgmtLevels = (post: Post): { key: string; value: string }[] => {
  return [
    {
      key: 'POST_MGMT_LEV_3',
      value: post ? post.mgmt_lev_3 || 'UNKNOWN' : 'UNKNOWN'
    },
    {
      key: 'POST_MGMT_LEV_1',
      value: post ? post.mgmt_lev_1 || 'UNKNOWN' : 'UNKNOWN'
    },
    {
      key: 'POST_MGMT_LEV_2',
      value: post ? post.mgmt_lev_2 || 'UNKNOWN' : 'UNKNOWN'
    }
  ];
};

const GetMgmtLevLabel = (mgmtLev: PostManagementLevel): string => {
  switch (mgmtLev) {
    case PostManagementLevel.Subregional:
      return 'POST_MGMT_LEV_OPT_1';
    case PostManagementLevel.Regional:
      return 'POST_MGMT_LEV_OPT_2';
    case PostManagementLevel.National:
      return 'POST_MGMT_LEV_OPT_3';
  }
  return 'POST_MGMT_LEV_OPT_0';
};

const FormatMgmtLev = (post: Post): string => GetMgmtLevLabel(post.mgmt_lev);

const FormatUser = (user_id: number, users: User[]): string => {
  const user = users ? users.find(u => u.id == user_id) : null;
  return user ? '"' + user.realname + '"' : '';
};


const GetStatusColor = (status: PostStatus | string) => {
  switch (status) {
    case PostStatus.Verified:
      return 'Verified';
    case PostStatus.Unverified:
      return 'Unverified';
    case PostStatus.Responded:
      return 'Responded';
    case PostStatus.Evaluated:
      return 'Evaluated';
    case PostStatus.Pending:
      return 'Pending';
    case PostStatus.InProgressVerification:
      return 'InProgressVerification';
    case PostStatus.Recibido:
      return 'Recibido';
    case PostStatus.Monitoreado:
      return 'Monitoreado';
    case PostStatus.Analizado:
      return 'Analizado';
    case PostStatus.Derivado:
      return 'Derivado';
    case PostStatus.Cerrado:
      return 'Cerrado';
    default:
      return 'Pending';
  }
};

//sp
const GetStatusLabelName = (status: PostStatus | string) => {
  switch (status) {
    case PostStatus.Verified:
      return 'Verificado';
    case PostStatus.Unverified:
      return 'No verificado';
    case PostStatus.Responded:
      return 'Respondido';
    case PostStatus.Evaluated:
      return 'Evaluado';
    case PostStatus.Pending:
      return 'Pendiente';
    case PostStatus.InProgressVerification:
      return 'Verificación en progreso';
    case PostStatus.Recibido:
      return 'Recibido';
    case PostStatus.Monitoreado:
      return 'Monitoreado';
    case PostStatus.Analizado:
      return 'Analizado';
    case PostStatus.Derivado:
      return 'Derivado';
    case PostStatus.Cerrado:
      return 'Cerrado';
    default:
      return 'Pendiente';
  }
};

const GetStatusLabelColor = (status: PostStatus | string) => {
  switch (status) {
    case PostStatus.Verified:
      return 'rgb(82, 255, 51)';
    case PostStatus.Unverified:
      return 'rgb(255, 51, 51)';
    case PostStatus.Responded:
      return 'rgb(51, 70, 255)';
    case PostStatus.Evaluated:
      return 'rgb(51, 197, 255)';
    case PostStatus.Pending:
    case 'draft':
      return 'rgb(255, 164, 51)';
    case PostStatus.InProgressVerification:
    case 'verification':
      return 'rgb(255, 255, 0)';
    case PostStatus.Recibido:
      return 'rgb(255, 164, 51)';
    case PostStatus.Monitoreado:
      return 'rgb(255, 255, 0)';
    case PostStatus.Analizado:
      return 'rgb(82, 255, 51)';
    case PostStatus.Derivado:
      return 'rgb(51, 70, 255)';
    case PostStatus.Cerrado:
      return 'rgb(51, 197, 255)';
    default:
      return 'rgb(255, 164, 51)';
  }
};

const GetAllStatusMapping = (
  scheme?: string | FormStatusScheme | 'all' | null
) => {
  let statuses: PostStatus[];
  if (scheme === 'all' || scheme == null) {
    statuses = GetAllWorkflowStatuses();
  } else {
    statuses = GetStatusesForScheme(scheme);
  }
  return statuses.map(status => ({
    title: GetStatusLabelName(status),
    color: GetStatusLabelColor(status)
  }));
};

const GetDropdownLabelName = (status: string) => {
  switch (status) {
    case 'Basic Post':
      return 'Create Incident';
    case 'Risk Report':
      return 'Create Risk';
    default:
      return status;
  }
};

const GetReportLabelName = (status: string) => {
  switch (status) {
    case 'Basic Post':
      return 'Incident Reports';
    default:
      return status;
  }
};

/** When API omits `form.name` on list payloads, resolve label from the forms catalog (display only). */
const resolveFormNameFromCatalog = (
  post: Post,
  catalog?: Form[] | null
): string => {
  if (!catalog?.length) {
    return '';
  }
  const fid =
    post.form?.id ??
    (post as unknown as { form_id?: number }).form_id;
  if (fid == null) {
    return '';
  }
  const match = catalog.find(f => Number(f.id) === Number(fid));
  const n = match?.name != null ? String(match.name).trim() : '';
  return n || '';
};

/** Title in lists/UI: saved title; if empty, form name from payload or from `formsCatalog` (never writes to DB). */
const GetPostDisplayTitle = (
  post: Post | null | undefined,
  formsCatalog?: Form[] | null
): string => {
  if (!post) {
    return '';
  }
  const raw = post.title != null ? String(post.title).trim() : '';
  if (raw) {
    return raw;
  }
  const embedded =
    post.form && post.form.name != null ? String(post.form.name).trim() : '';
  if (embedded) {
    return embedded;
  }
  return resolveFormNameFromCatalog(post, formsCatalog);
};


export const PostHelpers = {
  GetPostDisplayTitle,
  GetStatusLabel,
  GetSourceLabel,
  FormatStatus,
  GetReportLabelName,
  NormalizePostPriority,
  HasDisplayablePostContent,
  FormatPriority,
  FormatSource,
  FormatDate,
  ParseMgmtLevels,
  FormatMgmtLevels,
  GetDropdownLabelName,
  GetMgmtLevLabel,
  FormatMgmtLev,
  FormatUser,
  GetPostPriorityLebel,
  GetPriorityColorClass,
  FormatAuditValue,
  GetStatusColor,
  GetStatusLabelName,
  GetStatusLabelColor,
  GetAllStatusMapping,
  NormalizeStatusScheme,
  GetStatusSchemeFromForm,
  GetStatusesForScheme,
  GetAllWorkflowStatuses,
  CanPublishOnMap,
  GetConflictStatusLabel,
  IsOnpeStatus
};
